import { createDebugLogger } from '@videa/utils';

import type { VideaNotesAppointmentData } from '../types';

const debug = createDebugLogger('MicLoadingStallTelemetry');

const TAB_COUNTER_CHANNEL = 'videa_notes_tab_counter';
const TAB_COUNTER_WAIT_MS = 300;

const currentTabId =
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

let hasRegisteredResponder = false;

const registerTabResponder = () => {
  if (hasRegisteredResponder || typeof window === 'undefined') return;
  hasRegisteredResponder = true;

  try {
    // Lightweight per-tab responder so another tab can estimate how many
    // Videa tabs are open without shared storage dependencies.
    const responderChannel = new BroadcastChannel(TAB_COUNTER_CHANNEL);
    responderChannel.onmessage = (event: MessageEvent) => {
      const data = event.data as
        | {
            type?: 'tabs:ping' | 'tabs:pong';
            requestId?: string;
            fromTabId?: string;
          }
        | undefined;
      if (
        data?.type === 'tabs:ping' &&
        data.requestId &&
        data.fromTabId !== currentTabId
      ) {
        responderChannel.postMessage({
          type: 'tabs:pong',
          requestId: data.requestId,
          fromTabId: currentTabId,
        });
      }
    };
  } catch {
    // BroadcastChannel may be unavailable in some browser contexts.
  }
};

// Registered lazily on first telemetry call to avoid keeping a BroadcastChannel
// open in test environments (which prevents Jest from exiting).

const getOpenVideaTabCount = async (): Promise<number | null> => {
  if (typeof window === 'undefined') return null;

  try {
    const requestId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const peerTabIds = new Set<string>();
    const probeChannel = new BroadcastChannel(TAB_COUNTER_CHANNEL);

    probeChannel.onmessage = (event: MessageEvent) => {
      const data = event.data as
        | {
            type?: 'tabs:ping' | 'tabs:pong';
            requestId?: string;
            fromTabId?: string;
          }
        | undefined;
      if (
        data?.type === 'tabs:pong' &&
        data.requestId === requestId &&
        data.fromTabId &&
        data.fromTabId !== currentTabId
      ) {
        peerTabIds.add(data.fromTabId);
      }
    };

    probeChannel.postMessage({
      type: 'tabs:ping',
      requestId,
      fromTabId: currentTabId,
    });

    // Give peers a short window to respond, then derive count as peers + self.
    await new Promise((resolve) => setTimeout(resolve, TAB_COUNTER_WAIT_MS));
    probeChannel.close();
    return peerTabIds.size + 1;
  } catch {
    return null;
  }
};

const getDeviceMemoryGb = (): number | null => {
  const memory = (navigator as Navigator & { deviceMemory?: number })
    .deviceMemory;
  return typeof memory === 'number' ? memory : null;
};

const getJsHeapMemoryMb = () => {
  const perf = performance as Performance & {
    memory?: {
      usedJSHeapSize?: number;
      totalJSHeapSize?: number;
      jsHeapSizeLimit?: number;
    };
  };
  if (!perf.memory) return null;

  const toMb = (value?: number) =>
    typeof value === 'number'
      ? Math.round((value / (1024 * 1024)) * 100) / 100
      : null;

  return {
    usedMb: toMb(perf.memory.usedJSHeapSize),
    totalMb: toMb(perf.memory.totalJSHeapSize),
    limitMb: toMb(perf.memory.jsHeapSizeLimit),
  };
};


const buildReplicationSteps = (input: {
  hasMicrophonePermission: boolean;
  permissionState: string;
  hasSelectedDevice: boolean;
  canRecord: boolean;
  isMonitoringStreamPresent: boolean;
  isMonitoringStreamActive: boolean;
  hasAudioActivity: boolean;
  recorderStatus: string;
  loadingDurationMs: number;
}) => [
  'Open Voice Notes recorder UI and navigate to audio capture.',
  input.hasMicrophonePermission
    ? `Microphone permission is granted (state=${input.permissionState}).`
    : `Microphone permission is not granted (state=${input.permissionState}).`,
  input.hasSelectedDevice
    ? 'A microphone is selected in the dropdown.'
    : 'No microphone is selected in the dropdown.',
  `Click Start Recording and observe loading spinner for ${Math.round(
    input.loadingDurationMs / 1000,
  )}s+ while monitoringStream=${
    input.isMonitoringStreamPresent ? 'present' : 'null'
  }, streamActive=${input.isMonitoringStreamActive}, hasAudioActivity=${
    input.hasAudioActivity
  }, canRecord=${input.canRecord}, recorderStatus=${input.recorderStatus}.`,
];

export const reportMicLoadingStallToSentry = async (input: {
  loadingDurationMs: number;
  hasMicrophonePermission: boolean;
  permissionState: string;
  canRecord: boolean;
  selectedDeviceId: string | null;
  selectedDeviceLabel: string | null;
  hasAudioActivity: boolean;
  isMonitoringStreamPresent: boolean;
  isMonitoringStreamActive: boolean;
  recorderStatus: string;
  appointmentData: VideaNotesAppointmentData | null;
}) => {
  try {
    registerTabResponder();
    const openVideaTabCount = await getOpenVideaTabCount();
    const deviceMemoryGb = getDeviceMemoryGb();
    const jsHeapMemoryMb = getJsHeapMemoryMb();

    const sentry = await import('@sentry/browser');
    const replay = (sentry as any).getReplay?.();
    // Best-effort replay escalation: buffer/start replay around the stall report
    // so the session has diagnostics even if replay was not active yet.
    if (replay?.startBuffering) {
      replay.startBuffering();
    } else if (replay?.start && !replay?.isEnabled?.()) {
      replay.start();
    }

    const isLikelyInvalidOrMutedMicSelection =
      input.hasMicrophonePermission &&
      !!input.selectedDeviceId &&
      !input.canRecord &&
      (!input.hasAudioActivity || !input.isMonitoringStreamPresent);

    const replicationSteps = buildReplicationSteps({
      hasMicrophonePermission: input.hasMicrophonePermission,
      permissionState: input.permissionState,
      hasSelectedDevice: !!input.selectedDeviceId,
      canRecord: input.canRecord,
      isMonitoringStreamPresent: input.isMonitoringStreamPresent,
      isMonitoringStreamActive: input.isMonitoringStreamActive,
      hasAudioActivity: input.hasAudioActivity,
      recorderStatus: input.recorderStatus,
      loadingDurationMs: input.loadingDurationMs,
    });

    sentry.withScope((scope) => {
      scope.setLevel('warning');
      scope.setTag('feature', 'voice_notes');
      scope.setTag('issue_type', 'start_recording_loading_stall');
      scope.setTag('recorder_status', input.recorderStatus);
      scope.setTag('mic_permission_state', input.permissionState || 'unknown');
      scope.setTag(
        'has_selected_mic',
        input.selectedDeviceId ? 'true' : 'false',
      );
      scope.setTag(
        'likely_invalid_or_muted_mic',
        isLikelyInvalidOrMutedMicSelection ? 'true' : 'false',
      );
      scope.setTag(
        'loading_state_kind',
        input.isMonitoringStreamPresent
          ? 'disabled_or_inactive_mic'
          : 'indefinite_loading',
      );

      scope.setExtra('loading_duration_ms', input.loadingDurationMs);
      scope.setExtra('selected_device_id', input.selectedDeviceId);
      scope.setExtra('selected_device_label', input.selectedDeviceLabel);
      scope.setExtra('has_audio_activity', input.hasAudioActivity);
      scope.setExtra('can_record', input.canRecord);
      scope.setExtra(
        'has_microphone_permission',
        input.hasMicrophonePermission,
      );
      scope.setExtra(
        'is_monitoring_stream_present',
        input.isMonitoringStreamPresent,
      );
      scope.setExtra(
        'is_monitoring_stream_active',
        input.isMonitoringStreamActive,
      );
      scope.setExtra('open_videa_tab_count', openVideaTabCount);
      scope.setExtra(
        'has_multiple_videa_tabs_open',
        typeof openVideaTabCount === 'number' ? openVideaTabCount > 1 : null,
      );
      scope.setExtra('device_memory_gb', deviceMemoryGb);
      scope.setExtra('js_heap_memory_mb', jsHeapMemoryMb);
      scope.setExtra(
        'visit_identifiers',
        input.appointmentData
          ? {
              unifiedId: input.appointmentData.unifiedId,
              standalonePatientVisitId:
                input.appointmentData.standalonePatientVisitId,
              estimatedVisitId: input.appointmentData.estimatedVisitId,
            }
          : null,
      );
      scope.setExtra('replication_steps', replicationSteps);
      scope.setExtra(
        'user_agent',
        typeof navigator !== 'undefined' ? navigator.userAgent : null,
      );
      scope.setExtra(
        'page_url',
        typeof window !== 'undefined' ? window.location.href : null,
      );

      sentry.captureMessage(
        'Voice Notes: start recording button loading stalled for 10s+',
        'warning',
      );
    });
  } catch (error) {
    debug.error('Failed to send mic-loading stall telemetry:', error);
  }
};
